import React, { useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useOnboarding } from './useOnboarding';
import { clientTourSteps, practitionerTourSteps } from './tourSteps';

interface OnboardingTourProps {
  userType: 'client' | 'practitioner';
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ userType }) => {
  const {
    isRunning,
    setIsRunning,
    stepIndex,
    setStepIndex,
    endTour,
  } = useOnboarding(userType);

  const steps = userType === 'client' ? clientTourSteps : practitionerTourSteps;

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;

    // Handle tour completion or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      endTour();
      return;
    }

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      endTour();
    }
  }, [endTour, setStepIndex]);

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      spotlightClicks
      disableOverlayClose
      callback={handleJoyrideCallback}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      styles={{
        options: {
          primaryColor: '#D97706', // amber-600
          textColor: '#1f2937', // gray-800
          backgroundColor: '#ffffff',
          arrowColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 8,
          padding: 16,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 8,
        },
        tooltipContent: {
          fontSize: 14,
          lineHeight: 1.5,
        },
        buttonNext: {
          backgroundColor: '#D97706',
          borderRadius: 6,
          padding: '8px 16px',
          fontSize: 14,
          fontWeight: 500,
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: 8,
        },
        buttonSkip: {
          color: '#9ca3af',
          fontSize: 13,
        },
        spotlight: {
          borderRadius: 8,
        },
        beacon: {
          display: 'none',
        },
      }}
    />
  );
};

export default OnboardingTour;
